import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useSelector } from "react-redux";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import {
  ShoppingCart, ArrowLeftRight, CircleDollarSign, Undo2, Scale, Landmark,
  Wallet, SlidersHorizontal, Coins, User, RefreshCw, CalendarClock, Receipt,
  FileText, FileSignature, Package, Link2, CreditCard, BookmarkCheck, PenLine,
  Diamond, ArrowUpCircle, ArrowRightLeft, Percent, Building2, UserCircle,
  BadgeCheck, ShieldAlert, Lock, BarChart3, DoorOpen, Ticket, Calculator,
  WalletCards, ScrollText, Monitor, Cable, TrendingUp, FolderOpen, Leaf,
  Handshake, FlaskConical, Layers3, PackageOpen, LineChart, Banknote,
  Workflow, HeartPulse, Search, ChevronRight, ChevronDown, Check, X,
  Plus, Copy, KeyRound, Zap, Radio, AlertTriangle, Hexagon,
} from "lucide-react";
import { RootState } from "../../redux/store";

/* ─── THEME ───────────────────────────────────────────────────────────────
   Rebranded off the Ebuos wordmark: blue → pink → orange → yellow → green.
   This file has no external design-system dependency — every token below
   is local, and (as of this pass) so is every style: there's no injected
   `<style>` block anymore either. Every element carries its own
   `style={{...}}`, and the handful of pieces that need a hover/press state
   (buttons, beads, checkboxes, chips, rows, tags) are small local
   components that track that state with useState and recompute their own
   inline style. This page also no longer paints its own white background —
   it renders inside the dashboard shell's <main>, so it sits on whatever
   background that shell provides. ─────────────────────────────────────── */

const INK        = "#20233A";
const INK_DEEP   = "#151833";
const SLATE      = "#5B6178";
const FAINT      = "#9AA0B4";
const PEARL      = "#FFFFFF";
const MIST       = "#F4F5F9";
const CHROME     = "#E4E7F0";
const CHROME_DIM = "#C7CCDA";
const RED        = "#FF5C5C";
const RED_DEEP   = "#B23434";
const BEVEL_SM   = `inset 0 0 0 2px ${CHROME_DIM}`;

const EBUOS_BLUE   = "#4C6FEE";
const EBUOS_PINK   = "#E93E6D";
const EBUOS_ORANGE = "#F3821E";
const EBUOS_YELLOW = "#E8C51E";
const EBUOS_GREEN  = "#3FBE55";

const EBUOS_GRADIENT = `linear-gradient(90deg, ${EBUOS_BLUE} 0%, ${EBUOS_PINK} 28%, ${EBUOS_ORANGE} 52%, ${EBUOS_YELLOW} 74%, ${EBUOS_GREEN} 100%)`;

/* Single-color accent for small UI (checkboxes, radios, tags, focus rings) —
   a rainbow everywhere would be noise. Pulled from the middle of the mark. */
const ACCENT      = EBUOS_PINK;
const ACCENT_DEEP = "#A81E48";
const ACCENT_TINT = "#FDEAF0";

/* Warning amber already sits inside the brand range (between orange/yellow),
   so it's kept as-is rather than invented fresh. */
const GOLD      = { base: "#E0A72B", lit: "#F7CE5B", deep: "#9A6B00" };
/* Success green now matches the wordmark's "S" exactly instead of a
   generic sage. */
const SAGE      = EBUOS_GREEN;
const SAGE_DEEP = "#217A3B";

const FONT_LINK =
  "https://fonts.googleapis.com/css2?family=Fredoka:wght@500;600;700&family=Space+Grotesk:wght@400;500;600;700&family=IBM+Plex+Mono:wght@500;600&display=swap";

const API = "https://europe-west3-ebuosystem.cloudfunctions.net";
/* NOTE: region corrected from the original "europe-west1" to
   "europe-west3" — that's what functions.setGlobalOptions() in
   auth-webhooks/index.js actually deploys to. The old value would have
   404'd against every real function. */

type Ico = React.ComponentType<{ size?: number; strokeWidth?: number; color?: string }>;

/* ════════════════════════════════════════════════════════════════════
   EVENT CATALOG
   Every slot key must exist in SLOTS inside generateWebhookSecret.
   Kept identical to the backend's SLOTS object — same slots, same event
   lists, same order — so the two never drift apart. LIVE_EVENTS = what
   the gateway emits today; the rest are declared so merchants can
   subscribe ahead of implementation.
   ════════════════════════════════════════════════════════════════════ */

const LIVE_EVENTS = new Set([
  "checkout.session.completed",
  "payment_intent.succeeded", "payment_intent.payment_failed", "payment_intent.canceled",
  "charge.succeeded", "charge.refunded", "charge.refund.updated",
  "payout.paid", "payout.failed", "payout.created",
]);

type Row = [string, string];
interface RawSlot { slot: string; label: string; icon: Ico; blurb: string; v2?: boolean; rows: Row[] }

const CATALOG: RawSlot[] = [
  { slot: "checkout", label: "Checkout", icon: ShoppingCart, blurb: "Hosted checkout session lifecycle.", rows: [
    ["checkout.session.completed", "A Checkout Session completed successfully."],
    ["checkout.session.expired", "A Checkout Session expired."],
    ["checkout.session.async_payment_succeeded", "A delayed payment method finally succeeded."],
    ["checkout.session.async_payment_failed", "A delayed payment method failed."],
  ]},
  { slot: "payment_intent", label: "Payment Intent", icon: ArrowLeftRight, blurb: "The core payment object across every rail.", rows: [
    ["payment_intent.succeeded", "A payment intent completed payment successfully."],
    ["payment_intent.payment_failed", "A payment intent failed to create a payment."],
    ["payment_intent.canceled", "A payment intent was canceled."],
    ["payment_intent.created", "A new payment intent was created."],
    ["payment_intent.processing", "A payment intent started processing."],
    ["payment_intent.requires_action", "A payment intent needs further customer action."],
    ["payment_intent.amount_capturable_updated", "Funds are available to be captured."],
    ["payment_intent.partially_funded", "Funds were applied to a customer-balance intent."],
  ]},
  { slot: "charge", label: "Charge", icon: CircleDollarSign, blurb: "Individual money movements against an intent.", rows: [
    ["charge.succeeded", "A charge was successful."],
    ["charge.failed", "A charge attempt failed."],
    ["charge.pending", "A pending charge was created."],
    ["charge.captured", "A previously uncaptured charge was captured."],
    ["charge.expired", "An uncaptured charge expired."],
    ["charge.updated", "A charge description or metadata changed."],
    ["charge.refunded", "A charge was refunded, in full or in part."],
    ["charge.refund.updated", "A refund was updated on selected payment methods."],
  ]},
  { slot: "refund", label: "Refund", icon: Undo2, blurb: "Refund objects and their status changes.", rows: [
    ["refund.created", "A refund was created."],
    ["refund.updated", "A refund was updated."],
    ["refund.failed", "A refund failed to process."],
  ]},
  { slot: "chat", label: "Chat", icon: Handshake, blurb: "Customer support chat sessions and messages.", rows: [
    ["chat.session.created", "A new chat session was started."],
    ["chat.session.closed", "A chat session was closed."],
    ["chat.message.created", "A customer sent a message."],
    ["chat.message.completed", "The support agent replied."],
    ["chat.message.failed", "The support agent failed to reply."],
  ]},
  { slot: "chat_cancellation", label: "Chat — Cancellations", icon: Undo2, blurb: "Cancellation actions raised inside chat.", rows: [
    ["chat.action.cancel_requested", "The chat agent flagged a cancellation request."],
    ["chat.action.cancel_completed", "A chat-flagged cancellation was completed."],
    ["chat.action.cancel_rejected", "A chat-flagged cancellation was rejected."],
  ]},
  { slot: "chat_refund", label: "Chat — Refunds", icon: Undo2, blurb: "Refund actions raised inside chat.", rows: [
    ["chat.action.refund_requested", "The chat agent flagged a refund request."],
    ["chat.action.refund_completed", "A chat-flagged refund was completed."],
    ["chat.action.refund_rejected", "A chat-flagged refund was rejected."],
  ]},
  { slot: "chat_escalation", label: "Chat — Escalations", icon: AlertTriangle, blurb: "Conversations handed off to a human.", rows: [
    ["chat.escalation.created", "The chat agent escalated to a human teammate."],
    ["chat.escalation.assigned", "An escalation was assigned to a teammate."],
    ["chat.escalation.resolved", "An escalation was resolved."],
  ]},
  { slot: "dispute", label: "Dispute", icon: Scale, blurb: "Chargebacks, evidence, and fund movements.", rows: [
    ["charge.dispute.created", "A customer disputed a charge with their bank."],
    ["charge.dispute.updated", "A dispute was updated, usually with evidence."],
    ["charge.dispute.closed", "A dispute closed as lost, won, or warning_closed."],
    ["charge.dispute.funds_withdrawn", "Funds were removed due to a dispute."],
    ["charge.dispute.funds_reinstated", "Funds were reinstated after a dispute closed."],
  ]},
  { slot: "payout", label: "Payout", icon: Landmark, blurb: "Settlements to your bank account.", rows: [
    ["payout.paid", "A payout is expected in the destination account."],
    ["payout.failed", "A payout attempt failed."],
    ["payout.created", "A payout was created."],
    ["payout.canceled", "A payout was canceled."],
    ["payout.updated", "A payout was updated."],
    ["payout.reconciliation_completed", "Balance transactions in an automatic payout are queryable."],
  ]},
  { slot: "balance", label: "Balance", icon: Wallet, blurb: "Available balance changes.", rows: [
    ["balance.available", "Your balance was updated and funds are payable."],
  ]},
  { slot: "balance_settings", label: "Balance Settings", icon: SlidersHorizontal, blurb: "Balance configuration changes.", rows: [
    ["balance_settings.updated", "A balance settings status or property changed."],
  ]},
  { slot: "cash_balance", label: "Cash Balance", icon: Coins, blurb: "Customer cash balance reconciliation.", rows: [
    ["cash_balance.funds_available", "A positive cash balance remains after reconciliation."],
    ["customer_cash_balance_transaction.created", "A customer cash balance transaction was created."],
  ]},
  { slot: "customer", label: "Customer", icon: User, blurb: "Saved customers, sources, discounts, and tax IDs.", rows: [
    ["customer.created", "A new customer was created."],
    ["customer.updated", "A customer property changed."],
    ["customer.deleted", "A customer was deleted."],
    ["customer.discount.created", "A coupon was attached to a customer."],
    ["customer.discount.updated", "A customer switched from one coupon to another."],
    ["customer.discount.deleted", "A coupon was removed from a customer."],
    ["customer.source.created", "A new source was created for a customer."],
    ["customer.source.updated", "A source's details changed."],
    ["customer.source.deleted", "A source was removed from a customer."],
    ["customer.source.expiring", "A legacy card or source expires at month end."],
    ["customer.tax_id.created", "A tax ID was created for a customer."],
    ["customer.tax_id.updated", "A customer's tax ID was updated."],
    ["customer.tax_id.deleted", "A tax ID was deleted from a customer."],
  ]},
  { slot: "subscription", label: "Subscription", icon: RefreshCw, blurb: "Recurring plan lifecycle.", rows: [
    ["customer.subscription.created", "A customer signed up for a new plan."],
    ["customer.subscription.updated", "A subscription changed plan or status."],
    ["customer.subscription.deleted", "A customer's subscription ended."],
    ["customer.subscription.paused", "A subscription entered paused status."],
    ["customer.subscription.resumed", "A paused subscription resumed."],
    ["customer.subscription.trial_will_end", "A trial ends in three days, or ended early."],
    ["customer.subscription.pending_update_applied", "A pending subscription update was applied."],
    ["customer.subscription.pending_update_expired", "A pending update expired before payment."],
  ]},
  { slot: "subscription_schedule", label: "Subscription Schedule", icon: CalendarClock, blurb: "Phased subscription schedules.", rows: [
    ["subscription_schedule.created", "A subscription schedule was created."],
    ["subscription_schedule.updated", "A subscription schedule was updated."],
    ["subscription_schedule.completed", "A subscription schedule completed."],
    ["subscription_schedule.canceled", "A subscription schedule was canceled."],
    ["subscription_schedule.released", "A subscription schedule was released."],
    ["subscription_schedule.aborted", "A schedule aborted due to delinquency cancellation."],
    ["subscription_schedule.expiring", "A schedule expires in seven days."],
  ]},
  { slot: "invoice", label: "Invoice", icon: Receipt, blurb: "Invoice drafting, finalization, and payment.", rows: [
    ["invoice.created", "A new invoice was created."],
    ["invoice.updated", "An invoice changed."],
    ["invoice.deleted", "A draft invoice was deleted."],
    ["invoice.finalized", "A draft invoice was finalized into an open invoice."],
    ["invoice.finalization_failed", "A draft invoice could not be finalized."],
    ["invoice.paid", "An invoice was paid, including out-of-band."],
    ["invoice.overpaid", "An invoice was paid with a non-zero overpayment."],
    ["invoice.payment_succeeded", "An invoice payment attempt succeeded."],
    ["invoice.payment_failed", "An invoice payment attempt failed."],
    ["invoice.payment_action_required", "An invoice payment needs further user action."],
    ["invoice.payment_attempt_required", "An invoice needs an unsupported payment method."],
    ["invoice.sent", "An invoice email was sent."],
    ["invoice.upcoming", "An invoice will be created for a subscription soon."],
    ["invoice.will_be_due", "An invoice becomes due soon."],
    ["invoice.overdue", "An invoice passed its due date."],
    ["invoice.marked_uncollectible", "An invoice was marked uncollectible."],
    ["invoice.voided", "An invoice was voided."],
    ["invoice_payment.paid", "An InvoicePayment was successfully paid."],
    ["invoiceitem.created", "An invoice item was created."],
    ["invoiceitem.deleted", "An invoice item was deleted."],
  ]},
  { slot: "credit_note", label: "Credit Note", icon: FileText, blurb: "Credit notes against invoices.", rows: [
    ["credit_note.created", "A credit note was created."],
    ["credit_note.updated", "A credit note was updated."],
    ["credit_note.voided", "A credit note was voided."],
  ]},
  { slot: "quote", label: "Quote", icon: FileSignature, blurb: "Sales quotes and their lifecycle.", rows: [
    ["quote.created", "A quote was created."],
    ["quote.finalized", "A quote was finalized."],
    ["quote.accepted", "A quote was accepted."],
    ["quote.canceled", "A quote was canceled."],
    ["quote.will_expire", "A quote expires soon."],
  ]},
  { slot: "catalog", label: "Product Catalog", icon: Package, blurb: "Products, prices, plans, coupons, and promo codes.", rows: [
    ["product.created", "A product was created."],
    ["product.updated", "A product was updated."],
    ["product.deleted", "A product was deleted."],
    ["price.created", "A price was created."],
    ["price.updated", "A price was updated."],
    ["price.deleted", "A price was deleted."],
    ["plan.created", "A plan was created."],
    ["plan.updated", "A plan was updated."],
    ["plan.deleted", "A plan was deleted."],
    ["coupon.created", "A coupon was created."],
    ["coupon.updated", "A coupon was updated."],
    ["coupon.deleted", "A coupon was deleted."],
    ["promotion_code.created", "A promotion code was created."],
    ["promotion_code.updated", "A promotion code was updated."],
  ]},
  { slot: "payment_link", label: "Payment Link", icon: Link2, blurb: "Shareable payment links.", rows: [
    ["payment_link.created", "A payment link was created."],
    ["payment_link.updated", "A payment link was updated."],
  ]},
  { slot: "payment_method", label: "Payment Method", icon: CreditCard, blurb: "Stored payment instruments.", rows: [
    ["payment_method.attached", "A payment method was attached to a customer."],
    ["payment_method.updated", "A payment method was updated via the API."],
    ["payment_method.automatically_updated", "The network automatically updated card details."],
    ["payment_method.detached", "A payment method was detached from a customer."],
  ]},
  { slot: "setup_intent", label: "Setup Intent", icon: BookmarkCheck, blurb: "Saving payment methods for later use.", rows: [
    ["setup_intent.created", "A new setup intent was created."],
    ["setup_intent.succeeded", "A setup intent successfully saved a payment method."],
    ["setup_intent.setup_failed", "A setup intent failed to save a payment method."],
    ["setup_intent.requires_action", "A setup intent needs further customer action."],
    ["setup_intent.canceled", "A setup intent was canceled."],
  ]},
  { slot: "mandate", label: "Mandate", icon: PenLine, blurb: "Direct debit and recurring mandates.", rows: [
    ["mandate.updated", "A mandate was updated."],
  ]},
  { slot: "source", label: "Source", icon: Diamond, blurb: "Legacy source objects.", rows: [
    ["source.chargeable", "A source became chargeable."],
    ["source.canceled", "A source was canceled."],
    ["source.failed", "A source failed."],
    ["source.mandate_notification", "A source mandate notification is set to manual."],
    ["source.refund_attributes_required", "Refund attributes are required on a receiver source."],
    ["source.transaction.created", "A source transaction was created."],
    ["source.transaction.updated", "A source transaction was updated."],
  ]},
  { slot: "topup", label: "Top-up", icon: ArrowUpCircle, blurb: "Balance top-ups.", rows: [
    ["topup.created", "A top-up was created."],
    ["topup.succeeded", "A top-up succeeded."],
    ["topup.failed", "A top-up failed."],
    ["topup.reversed", "A top-up was reversed."],
    ["topup.canceled", "A top-up was canceled."],
  ]},
  { slot: "transfer", label: "Transfer", icon: ArrowRightLeft, blurb: "Transfers between accounts.", rows: [
    ["transfer.created", "A transfer was created."],
    ["transfer.updated", "A transfer's description or metadata changed."],
    ["transfer.reversed", "A transfer was reversed, including partially."],
  ]},
  { slot: "application_fee", label: "Application Fee", icon: Percent, blurb: "Platform fees on connected charges.", rows: [
    ["application_fee.created", "An application fee was created on a charge."],
    ["application_fee.refunded", "An application fee was refunded."],
    ["application_fee.refund.updated", "An application fee refund was updated."],
  ]},
  { slot: "account", label: "Account", icon: Building2, blurb: "Account status, external accounts, and OAuth.", rows: [
    ["account.updated", "An account status or property changed."],
    ["account.application.authorized", "A user authorized an application."],
    ["account.application.deauthorized", "A user deauthorized an application."],
    ["account.external_account.created", "An external account was created."],
    ["account.external_account.updated", "An external account was updated."],
    ["account.external_account.deleted", "An external account was deleted."],
    ["capability.updated", "A capability has new requirements or status."],
  ]},
  { slot: "person", label: "Person", icon: UserCircle, blurb: "People associated with an account.", rows: [
    ["person.created", "A person associated with an account was created."],
    ["person.updated", "A person associated with an account was updated."],
    ["person.deleted", "A person associated with an account was deleted."],
  ]},
  { slot: "identity", label: "Identity", icon: BadgeCheck, blurb: "Identity verification sessions.", rows: [
    ["identity.verification_session.created", "A verification session was created."],
    ["identity.verification_session.processing", "A verification session started processing."],
    ["identity.verification_session.verified", "A verification session was verified."],
    ["identity.verification_session.requires_input", "A verification session needs user input."],
    ["identity.verification_session.canceled", "A verification session was canceled."],
    ["identity.verification_session.redacted", "A verification session was redacted."],
  ]},
  { slot: "radar", label: "Radar & Review", icon: ShieldAlert, blurb: "Fraud warnings and manual reviews.", rows: [
    ["radar.early_fraud_warning.created", "An early fraud warning was created."],
    ["radar.early_fraud_warning.updated", "An early fraud warning was updated."],
    ["review.opened", "A payment review was opened."],
    ["review.closed", "A payment review was closed."],
  ]},
  { slot: "reserve", label: "Reserve", icon: Lock, blurb: "Reserve holds, plans, and releases.", rows: [
    ["reserve.hold.created", "A reserve hold was created."],
    ["reserve.hold.updated", "A reserve hold was updated."],
    ["reserve.plan.created", "A reserve plan was created."],
    ["reserve.plan.updated", "A reserve plan was updated."],
    ["reserve.plan.disabled", "A reserve plan was disabled."],
    ["reserve.plan.expired", "A reserve plan expired."],
    ["reserve.release.created", "A reserve release was created."],
  ]},
  { slot: "billing", label: "Billing", icon: BarChart3, blurb: "Meters, credit grants, and alerts.", rows: [
    ["billing.alert.triggered", "A custom alert threshold was met."],
    ["billing.credit_balance_transaction.created", "A credit balance transaction was created."],
    ["billing.credit_grant.created", "A credit grant was created."],
    ["billing.credit_grant.updated", "A credit grant was updated."],
    ["billing.meter.created", "A meter was created."],
    ["billing.meter.updated", "A meter was updated."],
    ["billing.meter.deactivated", "A meter was deactivated."],
    ["billing.meter.reactivated", "A meter was reactivated."],
    ["v1.billing.meter.error_report_triggered", "A meter has invalid async usage events."],
    ["v1.billing.meter.no_meter_found", "A meter ID is missing or invalid in usage events."],
  ]},
  { slot: "billing_portal", label: "Billing Portal", icon: DoorOpen, blurb: "Customer self-service portal.", rows: [
    ["billing_portal.configuration.created", "A portal configuration was created."],
    ["billing_portal.configuration.updated", "A portal configuration was updated."],
    ["billing_portal.session.created", "A portal session was created."],
  ]},
  { slot: "entitlements", label: "Entitlements", icon: Ticket, blurb: "Feature access derived from subscriptions.", rows: [
    ["entitlements.active_entitlement_summary.updated", "A customer's entitlements changed."],
  ]},
  { slot: "tax", label: "Tax", icon: Calculator, blurb: "Tax rates and settings.", rows: [
    ["tax.settings.updated", "Tax settings were updated."],
    ["tax_rate.created", "A tax rate was created."],
    ["tax_rate.updated", "A tax rate was updated."],
  ]},
  { slot: "issuing_card", label: "Issuing — Cards", icon: WalletCards, blurb: "Issued cards, cardholders, and tokens.", rows: [
    ["issuing_card.created", "A card was created."],
    ["issuing_card.updated", "A card was updated."],
    ["issuing_cardholder.created", "A cardholder was created."],
    ["issuing_cardholder.updated", "A cardholder was updated."],
    ["issuing_token.created", "A digital wallet token was created."],
    ["issuing_token.updated", "A digital wallet token was updated."],
    ["issuing_personalization_design.activated", "A personalization design was activated."],
    ["issuing_personalization_design.deactivated", "A personalization design was deactivated."],
    ["issuing_personalization_design.rejected", "A personalization design was rejected in review."],
    ["issuing_personalization_design.updated", "A personalization design was updated."],
  ]},
  { slot: "issuing_transaction", label: "Issuing — Transactions", icon: ScrollText, blurb: "Authorizations, transactions, and disputes.", rows: [
    ["issuing_authorization.created", "An authorization was created."],
    ["issuing_authorization.request", "A synchronous authorization request needs a decision."],
    ["issuing_authorization.updated", "An authorization was updated."],
    ["issuing_transaction.created", "An issuing transaction was created."],
    ["issuing_transaction.updated", "An issuing transaction was updated."],
    ["issuing_transaction.purchase_details_receipt_updated", "Receipt data was added to a transaction."],
    ["issuing_dispute.created", "An issuing dispute was created."],
    ["issuing_dispute.submitted", "An issuing dispute was submitted."],
    ["issuing_dispute.updated", "An issuing dispute was updated."],
    ["issuing_dispute.closed", "An issuing dispute was won, lost, or expired."],
    ["issuing_dispute.funds_reinstated", "Funds were reinstated for an issuing dispute."],
    ["issuing_dispute.funds_rescinded", "Funds were deducted for an issuing dispute."],
  ]},
  { slot: "terminal", label: "Terminal", icon: Monitor, blurb: "In-person reader actions.", rows: [
    ["terminal.reader.action_succeeded", "A reader action succeeded."],
    ["terminal.reader.action_failed", "A reader action failed."],
    ["terminal.reader.action_updated", "A reader action was updated."],
  ]},
  { slot: "financial_connections", label: "Financial Connections", icon: Cable, blurb: "Linked bank accounts and refreshes.", rows: [
    ["financial_connections.account.created", "A linked account was created."],
    ["financial_connections.account.deactivated", "A linked account became inactive."],
    ["financial_connections.account.reactivated", "A linked account became active again."],
    ["financial_connections.account.disconnected", "A linked account was disconnected."],
    ["financial_connections.account.account_numbers_updated", "Account numbers were updated."],
    ["financial_connections.account.refreshed_balance", "A balance refresh finished."],
    ["financial_connections.account.refreshed_ownership", "An ownership refresh finished."],
    ["financial_connections.account.refreshed_transactions", "A transaction refresh finished."],
    ["financial_connections.account.supported_payment_method_types_updated", "Supported payment method types changed."],
    ["financial_connections.account.expected_deactivation_date_updated", "An expected deactivation date changed."],
    ["financial_connections.account.upcoming_deactivation", "A linked account will become inactive."],
    ["financial_connections.account.upcoming_account_number_expiry", "A tokenized account number expires soon."],
    ["financial_connections.authorization.expected_deactivation_date_updated", "An authorization deactivation date changed."],
    ["financial_connections.authorization.upcoming_deactivation", "An authorization will become inactive."],
  ]},
  { slot: "reporting", label: "Reporting & Sigma", icon: TrendingUp, blurb: "Report runs and scheduled queries.", rows: [
    ["reporting.report_run.succeeded", "A report run completed successfully."],
    ["reporting.report_run.failed", "A report run failed to complete."],
    ["reporting.report_type.updated", "A report type was updated with new data."],
    ["sigma.scheduled_query_run.created", "A scheduled query run finished."],
  ]},
  { slot: "file", label: "File", icon: FolderOpen, blurb: "Generated files.", rows: [
    ["file.created", "A new generated file is available."],
  ]},
  { slot: "climate", label: "Climate", icon: Leaf, blurb: "Carbon removal orders and products.", rows: [
    ["climate.order.created", "A climate order was created."],
    ["climate.order.canceled", "A climate order was canceled."],
    ["climate.order.delayed", "A climate order was delayed."],
    ["climate.order.delivered", "A climate order was delivered."],
    ["climate.order.product_substituted", "A climate order's product was substituted."],
    ["climate.product.created", "A climate product was created."],
    ["climate.product.pricing_updated", "A climate product's pricing was updated."],
  ]},
  { slot: "shared_payment", label: "Shared Payment", icon: Handshake, blurb: "Granted and issued payment tokens.", rows: [
    ["shared_payment.issued_token.active", "An issued token became active."],
    ["shared_payment.issued_token.requires_action", "An issued token needs further action."],
    ["shared_payment.issued_token.used", "An issued token was used for a successful charge."],
    ["shared_payment.issued_token.deactivated", "An issued token was deactivated."],
    ["shared_payment.granted_token.deactivated", "A granted token was deactivated."],
  ]},
  { slot: "test_helpers", label: "Test Helpers", icon: FlaskConical, blurb: "Test clocks. Test mode only.", rows: [
    ["test_helpers.test_clock.created", "A test clock was created."],
    ["test_helpers.test_clock.advancing", "A test clock started advancing."],
    ["test_helpers.test_clock.ready", "A test clock became ready."],
    ["test_helpers.test_clock.internal_failure", "A test clock failed to advance."],
    ["test_helpers.test_clock.deleted", "A test clock was deleted."],
  ]},

  /* ── v2 ── */
  { slot: "v2_account", label: "Account", icon: Building2, blurb: "Accounts v2 core and configuration.", v2: true, rows: [
    ["v2.core.account.created", "An account was created."],
    ["v2.core.account.updated", "An account was updated."],
    ["v2.core.account.closed", "An account was closed."],
    ["v2.core.account[identity].updated", "An identity was updated."],
    ["v2.core.account[defaults].updated", "Account defaults were created or updated."],
    ["v2.core.account[requirements].updated", "Account requirements were updated."],
    ["v2.core.account[future_requirements].updated", "Future requirements were updated."],
    ["v2.core.account[configuration.customer].updated", "Customer configuration was updated."],
    ["v2.core.account[configuration.customer].capability_status_updated", "Customer capability status changed."],
    ["v2.core.account[configuration.merchant].updated", "Merchant configuration was updated."],
    ["v2.core.account[configuration.merchant].capability_status_updated", "Merchant capability status changed."],
    ["v2.core.account[configuration.recipient].updated", "Recipient configuration was updated."],
    ["v2.core.account[configuration.recipient].capability_status_updated", "Recipient capability status changed."],
    ["v2.core.account_link.returned", "A generated AccountLink was completed."],
    ["v2.core.account_person.created", "A person was created."],
    ["v2.core.account_person.updated", "A person was updated."],
    ["v2.core.account_person.deleted", "A person was deleted."],
  ]},
  { slot: "v2_batch_job", label: "Batch Job", icon: Layers3, blurb: "Bulk job processing.", v2: true, rows: [
    ["v2.core.batch_job.created", "A batch job was created."],
    ["v2.core.batch_job.ready_for_upload", "A batch job was submitted."],
    ["v2.core.batch_job.upload_timeout", "A file was not uploaded in time."],
    ["v2.core.batch_job.validating", "A batch job entered validation."],
    ["v2.core.batch_job.validation_failed", "A batch job failed validation."],
    ["v2.core.batch_job.updated", "A batch job was updated."],
    ["v2.core.batch_job.completed", "A batch job completed."],
    ["v2.core.batch_job.batch_failed", "A batch job failed."],
    ["v2.core.batch_job.canceled", "A batch job was canceled."],
    ["v2.core.batch_job.timeout", "A batch job timed out."],
  ]},
  { slot: "v2_commerce", label: "Product Catalog Imports", icon: PackageOpen, blurb: "Bulk catalog imports.", v2: true, rows: [
    ["v2.commerce.product_catalog.imports.processing", "A catalog import started processing."],
    ["v2.commerce.product_catalog.imports.succeeded", "A catalog import passed validation."],
    ["v2.commerce.product_catalog.imports.succeeded_with_errors", "A catalog import processed with some row errors."],
    ["v2.commerce.product_catalog.imports.failed", "A catalog import failed."],
  ]},
  { slot: "v2_reporting", label: "Data Reporting", icon: LineChart, blurb: "Query runs.", v2: true, rows: [
    ["v2.data.reporting.query_run.created", "A query run was created."],
    ["v2.data.reporting.query_run.updated", "A query run was updated."],
    ["v2.data.reporting.query_run.succeeded", "A query run completed successfully."],
    ["v2.data.reporting.query_run.failed", "A query run failed to complete."],
  ]},
  { slot: "v2_money_management", label: "Money Management", icon: Banknote, blurb: "Outbound payments and transfers.", v2: true, rows: [
    ["v2.money_management.outbound_payment.under_review", "An outbound payment was placed under review."],
    ["v2.money_management.outbound_transfer.under_review", "An outbound transfer was placed under review."],
  ]},
  { slot: "v2_workflow", label: "Extend Workflow", icon: Workflow, blurb: "Workflow runs.", v2: true, rows: [
    ["v2.extend.workflow_run.started", "A workflow run started."],
    ["v2.extend.workflow_run.succeeded", "A workflow run succeeded."],
    ["v2.extend.workflow_run.failed", "A workflow run failed."],
  ]},
  { slot: "v2_health", label: "Health", icon: HeartPulse, blurb: "Event generation health alerts.", v2: true, rows: [
    ["v2.core.health.event_generation_failure.resolved", "An event generation failure alert resolved."],
  ]},
];

export interface SlotEvent { id: string; desc: string; live: boolean }
export interface SlotDef { slot: string; label: string; icon: Ico; blurb: string; v2: boolean; events: SlotEvent[] }

export const WEBHOOK_SLOTS: SlotDef[] = CATALOG.map(c => ({
  slot: c.slot, label: c.label, icon: c.icon, blurb: c.blurb, v2: !!c.v2,
  events: c.rows.map(([id, desc]) => ({ id, desc, live: LIVE_EVENTS.has(id) })),
}));

const slotDef = (slot: string) => WEBHOOK_SLOTS.find(s => s.slot === slot)!;
const defaultEventsFor = (slot: string) => {
  const d = slotDef(slot);
  const live = d.events.filter(e => e.live).map(e => e.id);
  return new Set(live.length ? live : d.events.map(e => e.id));
};

/* Server shape derived from merchant.webhooks[slot] on GET /getMerchant —
   never contains secrets. */
interface SlotState {
  slot: string; urlName: string | null; urlEndpoint: string | null;
  enabled: boolean; configured: boolean; has_secret: boolean;
  events: string[]; rotated_at: number | null;
}
interface SecretPair { slot: string; secret_test: string; secret_live: string; rotated: boolean }

type Step = 1 | 2 | 3 | 4;

/* ═══════════════════════════════════════════════════════════════════════
   STYLE HELPERS
   ═══════════════════════════════════════════════════════════════════════ */

const mono = (extra?: React.CSSProperties): React.CSSProperties => ({
  fontFamily: "'IBM Plex Mono',ui-monospace,monospace",
  fontWeight: 600, fontSize: 10, letterSpacing: ".2em", textTransform: "uppercase",
  ...extra,
});

const heading = (size: number, extra?: React.CSSProperties): React.CSSProperties => ({
  fontFamily: "'Fredoka',system-ui,sans-serif", fontWeight: 600, margin: 0,
  letterSpacing: "-.03em", lineHeight: 1, fontSize: size, ...extra,
});

const plateStyle = (extra?: React.CSSProperties): React.CSSProperties => ({
  position: "relative", background: PEARL, borderRadius: 26, padding: 22, overflow: "hidden",
  boxShadow: `${BEVEL_SM}, 0 8px 0 ${CHROME_DIM}, 0 10px 24px -16px rgba(20,20,43,.12)`,
  ...extra,
});

const perfStyle = (extra?: React.CSSProperties): React.CSSProperties => ({
  height: 2, background: `repeating-linear-gradient(90deg, ${CHROME_DIM} 0 7px, transparent 7px 15px)`,
  ...extra,
});

const footStyle: React.CSSProperties = {
  display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12,
  marginTop: 20, paddingTop: 16, boxShadow: `inset 0 2px 0 ${CHROME}`,
};

const recapStyle: React.CSSProperties = {
  background: MIST, borderRadius: 16, padding: "12px 15px", marginBottom: 20,
  display: "flex", flexWrap: "wrap", gap: "6px 20px", fontSize: 12, color: SLATE,
  boxShadow: `inset 0 0 0 2px ${CHROME_DIM}`,
};

const warnStyle: React.CSSProperties = {
  display: "flex", alignItems: "flex-start", gap: 10, background: "#FFF6E2", borderRadius: 16,
  padding: "13px 16px", marginBottom: 20, fontSize: 12, color: GOLD.deep, lineHeight: 1.7,
  boxShadow: `inset 0 0 0 2px ${GOLD.base}55`,
};

const secretBoxStyle: React.CSSProperties = {
  flex: 1, minWidth: 0, background: MIST, borderRadius: 15, padding: "12px 14px",
  fontSize: 11, color: INK, wordBreak: "break-all", textTransform: "none", letterSpacing: 0,
  fontFamily: "'IBM Plex Mono',ui-monospace,monospace",
  boxShadow: `inset 0 0 0 2.5px ${CHROME_DIM}`,
};

const evRowStyle = (opts: { isAll?: boolean; isLast?: boolean }): React.CSSProperties => ({
  display: "flex", alignItems: "flex-start", gap: 11, cursor: "pointer",
  padding: opts.isAll ? "7px 0 9px" : "9px 0",
  boxShadow: opts.isLast ? "none" : `inset 0 -2px 0 ${CHROME}`,
});

/* ═══════════════════════════════════════════════════════════════════════
   LOCAL PRESENTATIONAL COMPONENTS
   ═══════════════════════════════════════════════════════════════════════ */

function Spinner({ size = 30 }: { size?: number }) {
  const reduceMotion = useReducedMotion();
  const thick = size > 20;
  return (
    <motion.div
      style={{
        display: "inline-block", width: size, height: size, borderRadius: "50%",
        border: `${thick ? 4 : 2}px solid ${CHROME}`, borderTopColor: ACCENT,
      }}
      animate={reduceMotion ? { rotate: 0 } : { rotate: 360 }}
      transition={reduceMotion ? { duration: 0 } : { repeat: Infinity, ease: "linear", duration: 0.9 }}
    />
  );
}

/** Chrome sphere with a vector glyph — the emoji replacement. */
function IconBead({
  icon: Icon, size = 36, active, tone, iconSize, iconStrokeWidth = 2.3, style,
}: { icon: Ico; size?: number; active?: boolean; tone?: "sage"; iconSize?: number; iconStrokeWidth?: number; style?: React.CSSProperties }) {
  const glossy = `radial-gradient(circle at 34% 26%, #fff 0%, ${CHROME} 44%, ${CHROME_DIM} 100%)`;
  let bg = glossy, color = INK_DEEP;
  let shadow = `inset 0 -5px 8px rgba(4,8,32,.16), inset 0 3px 4px rgba(255,255,255,.75), 0 4px 0 ${CHROME_DIM}, 0 9px 14px -8px rgba(4,8,32,.35)`;
  if (tone === "sage") {
    bg = `radial-gradient(circle at 34% 26%, rgba(255,255,255,.95) 0%, rgba(255,255,255,.16) 38%, transparent 60%), ${SAGE}`;
    color = "#fff";
    shadow = `inset 0 -5px 8px rgba(4,8,32,.2), 0 4px 0 ${SAGE_DEEP}, 0 9px 14px -8px rgba(4,8,32,.4)`;
  } else if (active) {
    bg = EBUOS_GRADIENT; color = "#fff";
    shadow = `inset 0 -5px 8px rgba(4,8,32,.28), inset 0 3px 4px rgba(255,255,255,.45), 0 4px 0 ${ACCENT_DEEP}, 0 9px 14px -8px rgba(4,8,32,.45)`;
  }
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", justifyContent: "center", borderRadius: "50%", flexShrink: 0,
      width: size, height: size, background: bg, color, boxShadow: shadow,
      transition: "box-shadow .2s, background .2s, color .2s",
      ...style,
    }}>
      <Icon size={iconSize ?? size * 0.45} strokeWidth={iconStrokeWidth} />
    </span>
  );
}

/** Beveled checkbox that presses in when checked. */
function Check3D({ checked, onChange }: { checked: boolean; onChange: (v: boolean) => void }) {
  const [hover, setHover] = useState(false);
  const shadow = checked
    ? `inset 0 0 0 2.5px ${ACCENT}, inset 0 2px 3px rgba(255,255,255,.35), 0 1px 0 ${ACCENT_DEEP}`
    : `inset 0 0 0 2.5px ${hover ? ACCENT : CHROME_DIM}, 0 3px 0 ${CHROME_DIM}66`;
  return (
    <span
      role="checkbox" aria-checked={checked} tabIndex={0}
      onClick={e => { e.preventDefault(); onChange(!checked); }}
      onKeyDown={e => { if (e.key === " " || e.key === "Enter") { e.preventDefault(); onChange(!checked); } }}
      onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}
      style={{
        width: 19, height: 19, borderRadius: 7, flexShrink: 0, cursor: "pointer", marginTop: 1,
        display: "inline-flex", alignItems: "center", justifyContent: "center", color: "#fff",
        background: checked ? ACCENT : PEARL, boxShadow: shadow,
        transform: checked ? "translateY(2px)" : "translateY(0)",
        transition: "transform .14s cubic-bezier(.2,.9,.3,1.4), box-shadow .14s, background .14s",
      }}
    >
      {checked && <Check size={11} strokeWidth={3.6} />}
    </span>
  );
}

/** Recessed radio sphere. */
function Radio3D({ on }: { on: boolean }) {
  return (
    <span style={{
      width: 17, height: 17, borderRadius: "50%", flexShrink: 0, background: PEARL,
      boxShadow: on
        ? `inset 0 0 0 5px ${ACCENT}, inset 0 0 0 8px ${PEARL}, inset 0 0 0 10px ${ACCENT}`
        : `inset 0 0 0 2.5px ${CHROME_DIM}`,
      transition: "box-shadow .16s",
    }} />
  );
}

function Btn({
  children, onClick, disabled, variant = "default", size = "md", style,
}: {
  children: React.ReactNode; onClick?: () => void; disabled?: boolean;
  variant?: "default" | "primary" | "ghost"; size?: "md" | "sm"; style?: React.CSSProperties;
}) {
  const reduceMotion = useReducedMotion();
  const [hover, setHover] = useState(false);
  const [active, setActive] = useState(false);
  const isSm = size === "sm";

  let bg: string = PEARL, color: string = INK_DEEP;
  if (variant === "primary") { bg = EBUOS_GRADIENT; color = "#fff"; }
  if (variant === "ghost") { bg = hover ? PEARL : MIST; color = INK_DEEP; }

  const lift = disabled ? 0 : active ? (variant === "ghost" ? 2 : 3) : hover ? (variant === "ghost" ? -2 : -3) : 0;
  const shadow = variant === "ghost"
    ? `inset 0 0 0 2.5px ${hover ? ACCENT : CHROME_DIM}`
    : `${BEVEL_SM}, 0 ${active ? 1 : hover ? (isSm ? 6 : 9) : (isSm ? 4 : 6)}px 0 ${ACCENT_DEEP}`;

  return (
    <button
      type="button" onClick={onClick} disabled={disabled}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => { setHover(false); setActive(false); }}
      onMouseDown={() => setActive(true)}
      onMouseUp={() => setActive(false)}
      style={{
        display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 9, whiteSpace: "nowrap",
        fontFamily: "'Fredoka',sans-serif", fontWeight: 600,
        fontSize: isSm ? 12.5 : 14, padding: isSm ? "10px 17px" : "13px 22px", borderRadius: 999,
        background: bg, color, boxShadow: shadow, border: "none",
        cursor: disabled ? "default" : "pointer", opacity: disabled ? 0.65 : 1,
        transform: `translateY(${lift}px)`,
        transition: reduceMotion ? "none" : "transform .18s cubic-bezier(.2,.9,.3,1.4), box-shadow .18s, background .15s",
        textShadow: variant === "primary" ? "0 1px 2px rgba(0,0,0,.18)" : "none",
        ...style,
      }}
    >
      {children}
    </button>
  );
}

function CloseBtn({ onClick, size = 30 }: { onClick: () => void; size?: number }) {
  const [hover, setHover] = useState(false);
  const [active, setActive] = useState(false);
  return (
    <button
      type="button" onClick={onClick} aria-label="Close"
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => { setHover(false); setActive(false); }}
      onMouseDown={() => setActive(true)}
      onMouseUp={() => setActive(false)}
      style={{
        display: "inline-flex", alignItems: "center", justifyContent: "center", width: size, height: size,
        borderRadius: "50%", flexShrink: 0, cursor: "pointer", color: INK_DEEP, border: "none",
        background: `radial-gradient(circle at 34% 26%, #fff 0%, ${CHROME} 44%, ${CHROME_DIM} 100%)`,
        boxShadow: `inset 0 -3px 5px rgba(4,8,32,.14), 0 ${active ? 1 : hover ? 5 : 3}px 0 ${CHROME_DIM}`,
        transform: `translateY(${active ? 2 : hover ? -2 : 0}px)`,
        transition: "transform .16s, box-shadow .16s",
      }}
    >
      <X size={size >= 34 ? 15 : 14} strokeWidth={3} />
    </button>
  );
}

function TextField({
  icon: Icon, value, onChange, placeholder, invalid, style,
}: { icon?: Ico; value: string; onChange: (v: string) => void; placeholder?: string; invalid?: boolean; style?: React.CSSProperties }) {
  const [focused, setFocused] = useState(false);
  return (
    <div style={{
      display: "flex", alignItems: "center", gap: 10, padding: "11px 14px", borderRadius: 16, background: MIST,
      boxShadow: invalid ? `inset 0 0 0 2.5px ${RED}` : `inset 0 0 0 2px ${focused ? ACCENT : CHROME_DIM}`,
      transition: "box-shadow .15s",
      ...style,
    }}>
      {Icon && <Icon size={14} color={FAINT} />}
      <input
        value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder}
        onFocus={() => setFocused(true)} onBlur={() => setFocused(false)}
        style={{ flex: 1, minWidth: 0, border: "none", outline: "none", background: "transparent", fontSize: 13, fontFamily: "'Space Grotesk',sans-serif", color: INK }}
      />
    </div>
  );
}

function PickRow({
  children, active, disabled, onClick, marginBottom,
}: { children: React.ReactNode; active: boolean; disabled?: boolean; onClick?: () => void; marginBottom?: number }) {
  const [hover, setHover] = useState(false);
  const lift = active ? 3 : (hover && !disabled ? -2 : 0);
  const shadow = active
    ? `inset 0 0 0 2.5px ${ACCENT}, 0 2px 0 ${ACCENT_DEEP}55`
    : `inset 0 0 0 2.5px ${CHROME_DIM}, 0 ${hover && !disabled ? 7 : 5}px 0 ${CHROME_DIM}66`;
  return (
    <button
      type="button" onClick={disabled ? undefined : onClick} disabled={disabled}
      onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}
      style={{
        display: "flex", alignItems: "center", gap: 12, width: "100%", padding: "12px 14px", borderRadius: 18,
        textAlign: "left", background: active ? ACCENT_TINT : PEARL, border: "none",
        cursor: disabled ? "not-allowed" : "pointer", opacity: disabled ? 0.5 : 1,
        boxShadow: shadow, transform: `translateY(${lift}px)`, marginBottom,
        transition: "transform .16s cubic-bezier(.2,.9,.3,1.4), box-shadow .16s, background .16s",
      }}
    >
      {children}
    </button>
  );
}

function Tag({ children, tone }: { children: React.ReactNode; tone?: "blue" | "gold" }) {
  let bg = MIST, color = FAINT, shadow = `inset 0 0 0 1.5px ${CHROME_DIM}`, marginLeft: number | undefined;
  if (tone === "blue") { bg = ACCENT_TINT; color = ACCENT; shadow = `inset 0 0 0 1.5px ${ACCENT}44`; }
  if (tone === "gold") { bg = "#FFF6E2"; color = GOLD.deep; shadow = `inset 0 0 0 1.5px ${GOLD.base}55`; marginLeft = 7; }
  return (
    <span style={{
      fontFamily: "'IBM Plex Mono',monospace", fontWeight: 700, fontSize: 9, letterSpacing: ".08em", textTransform: "uppercase",
      padding: "2px 6px", borderRadius: 7, background: bg, color, boxShadow: shadow, marginLeft,
    }}>
      {children}
    </span>
  );
}

function Pill({ on, children }: { on: boolean; children: React.ReactNode }) {
  return (
    <span style={{
      fontFamily: "'IBM Plex Mono',monospace", fontWeight: 600, fontSize: 9, letterSpacing: ".16em", textTransform: "uppercase",
      padding: "6px 12px", borderRadius: 999, whiteSpace: "nowrap",
      background: on ? "#E8FBF2" : MIST, color: on ? SAGE_DEEP : FAINT,
      boxShadow: `inset 0 0 0 1.5px ${on ? SAGE + "55" : CHROME_DIM}`,
    }}>
      {children}
    </span>
  );
}

function Chip({ children, onClick }: { children: React.ReactNode; onClick: () => void }) {
  const [hover, setHover] = useState(false);
  const [active, setActive] = useState(false);
  return (
    <button
      type="button" onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => { setHover(false); setActive(false); }}
      onMouseDown={() => setActive(true)}
      onMouseUp={() => setActive(false)}
      style={{
        display: "inline-flex", alignItems: "center", gap: 7, padding: "9px 14px", borderRadius: 999,
        fontFamily: "'Fredoka',sans-serif", fontWeight: 600, fontSize: 12.5,
        color: hover ? ACCENT : INK_DEEP, background: PEARL, border: "none", cursor: "pointer",
        boxShadow: `inset 0 0 0 2.5px ${hover ? ACCENT : CHROME_DIM}, 0 ${active ? 1 : 4}px 0 ${CHROME_DIM}66`,
        transform: `translateY(${active ? 2 : hover ? -2 : 0}px)`,
        transition: "transform .16s cubic-bezier(.2,.9,.3,1.4), box-shadow .16s, color .15s",
      }}
    >
      {children}
    </button>
  );
}

function StepCircle({ n, state }: { n: number; state: "pending" | "active" | "done" }) {
  let bg = PEARL, color = FAINT, shadow = `inset 0 0 0 2px ${CHROME_DIM}, 0 3px 0 ${CHROME_DIM}55`;
  if (state === "active") { bg = EBUOS_GRADIENT; color = "#fff"; shadow = `inset 0 2px 3px rgba(255,255,255,.4), 0 3px 0 ${ACCENT_DEEP}`; }
  if (state === "done") {
    bg = `radial-gradient(circle at 34% 26%, rgba(255,255,255,.9) 0%, rgba(255,255,255,.14) 40%, transparent 62%), ${SAGE}`;
    color = "#fff"; shadow = `inset 0 2px 3px rgba(255,255,255,.4), 0 3px 0 ${SAGE_DEEP}`;
  }
  return (
    <span style={{
      width: 27, height: 27, borderRadius: "50%", flexShrink: 0, display: "inline-flex", alignItems: "center", justifyContent: "center",
      fontFamily: "'Fredoka',sans-serif", fontSize: 11.5, fontWeight: 700, background: bg, color, boxShadow: shadow,
    }}>
      {state === "done" ? <Check size={12} strokeWidth={3.4} /> : n}
    </span>
  );
}

function StepBar({ step }: { step: Step }) {
  const steps = ["Events", "Destination", "Configure"];
  return (
    <div style={{ display: "flex", alignItems: "center", marginBottom: 26 }}>
      {steps.map((label, i) => {
        const n = i + 1, done = step > n, active = step === n;
        return (
          <React.Fragment key={n}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <StepCircle n={n} state={done ? "done" : active ? "active" : "pending"} />
              <span style={{ fontSize: 11.5, fontWeight: active ? 600 : 500, color: active ? INK : FAINT, whiteSpace: "nowrap" }}>{label}</span>
            </div>
            {i < steps.length - 1 && <div style={{ flex: 1, height: 3, borderRadius: 2, margin: "0 10px", background: step > n ? SAGE : CHROME }} />}
          </React.Fragment>
        );
      })}
    </div>
  );
}

function FieldGroup({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ marginBottom: 16 }}>
      <label style={mono({ color: FAINT, display: "block", marginBottom: 7 })}>{label}</label>
      {children}
    </div>
  );
}

/* ════════════════════════════════════════════════════════════════════
   MAIN
   ════════════════════════════════════════════════════════════════════ */
export default function Webhooks({ merchantId: merchantIdProp }: { merchantId?: string }) {
  /* merchantId comes from the route/dashboard prop when given; otherwise
     fall back to the currently-promoted merchant in the slice — same
     source MerchantDashboard uses. No mode, no live/test key lookup. */
  const currentMerchantId = useSelector((s: RootState) => s.merchant.currentMerchant?.merchantId ?? "");
  const merchantId = merchantIdProp || currentMerchantId;

  const [isMobile, setIsMobile] = useState(window.innerWidth < 800);
  useEffect(() => {
    const h = () => setIsMobile(window.innerWidth < 800);
    window.addEventListener("resize", h);
    return () => window.removeEventListener("resize", h);
  }, []);

  const [slots, setSlots] = useState<SlotState[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  /* NOTE — backend gap: auth-webhooks/index.js only defines
     registerMerchant, rotateMerchantKeys, getMerchant, generateWebhookSecret,
     verifyWebhook, and retryWebhooks. There is no listWebhookSlots endpoint.
     A merchant's webhook state DOES exist server-side (merchant.webhooks in
     Firestore), but the only way to read it today is GET /getMerchant, which
     authenticates with an API key (Authorization: Bearer sk_/pk_/rk_...),
     not a bare merchantId — and a browser dashboard has no business holding
     a secret key. Either add a merchantId-scoped read endpoint (mirroring
     how generateWebhookSecret itself works) or read merchant.webhooks
     straight from Firestore via a security-rule-scoped client read. Left
     as-is below so the rest of the wizard has something to compile against;
     this call will 404 until one of those exists. */
  const loadSlots = useCallback(async () => {
    if (!merchantId) { setLoading(false); return; }
    try {
      const res = await fetch(`${API}/listWebhookSlots?merchantId=${encodeURIComponent(merchantId)}`);
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error?.message || "Failed to load endpoints");
      setSlots(data.data || []);
    } catch (e: any) { setError(e.message || "Network error"); }
    finally { setLoading(false); }
  }, [merchantId]);

  useEffect(() => { loadSlots(); }, [loadSlots]);

  const stateFor = (slot: string) => slots.find(s => s.slot === slot);
  const configured = WEBHOOK_SLOTS.filter(d => stateFor(d.slot)?.configured);

  /* ── wizard ── */
  const [showWizard, setShowWizard] = useState(false);
  const [step, setStep] = useState<Step>(1);
  const [activeSlot, setActiveSlot] = useState("checkout");
  const [selected, setSelected] = useState<Set<string>>(defaultEventsFor("checkout"));
  const [query, setQuery] = useState("");
  const [destType, setDestType] = useState<"webhook" | "stream">("webhook");
  const [urlName, setUrlName] = useState("");
  const [urlEndpoint, setUrlEndpoint] = useState("");
  const [busy, setBusy] = useState(false);
  const [wizError, setWizError] = useState<string | null>(null);
  const [secrets, setSecrets] = useState<SecretPair | null>(null);
  const [copied, setCopied] = useState<string | null>(null);
  const [rotateBusy, setRotateBusy] = useState<string | null>(null);

  const openWizard = (slot?: string) => {
    const s = slot || "checkout";
    const st = stateFor(s);
    setActiveSlot(s);
    setSelected(st?.events?.length ? new Set(st.events) : defaultEventsFor(s));
    setUrlName(st?.urlName || "");
    setUrlEndpoint(st?.urlEndpoint || "");
    setQuery(""); setDestType("webhook"); setWizError(null); setSecrets(null);
    setStep(1); setShowWizard(true);
  };

  const closeWizard = () => { setShowWizard(false); setSecrets(null); setWizError(null); };

  const save = async () => {
    setWizError(null);
    if (!urlName.trim()) { setWizError("Destination name is required"); return; }
    if (!urlEndpoint.startsWith("https://")) { setWizError("Endpoint URL must start with https://"); return; }
    setBusy(true);
    try {
      const res = await fetch(`${API}/generateWebhookSecret`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          merchantId, slot: activeSlot, urlName: urlName.trim(), urlEndpoint: urlEndpoint.trim(),
          events: Array.from(selected), enabled: true,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error?.message || "Failed to save endpoint");
      setSecrets({ slot: activeSlot, secret_test: data.secret_test, secret_live: data.secret_live, rotated: !!data.rotated });
      setStep(4);
      await loadSlots();
    } catch (e: any) { setWizError(e.message || "Network error"); }
    finally { setBusy(false); }
  };

  const rotate = async (slot: string) => {
    const st = stateFor(slot);
    if (!st?.urlEndpoint) return;
    setRotateBusy(slot);
    try {
      const res = await fetch(`${API}/generateWebhookSecret`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ merchantId, slot, urlName: st.urlName, urlEndpoint: st.urlEndpoint, events: st.events, rotate: true }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error?.message || "Rotation failed");
      setActiveSlot(slot);
      setSecrets({ slot, secret_test: data.secret_test, secret_live: data.secret_live, rotated: true });
      setUrlEndpoint(st.urlEndpoint); setUrlName(st.urlName || "");
      setSelected(new Set(st.events)); setStep(4); setShowWizard(true);
      await loadSlots();
    } catch (e: any) { setError(e.message || "Rotation failed"); }
    finally { setRotateBusy(null); }
  };

  /* Previously POSTed to a nonexistent /updateWebhookSlot endpoint.
     generateWebhookSecret already does this: it re-saves a slot without
     rotating secrets when `rotate` is omitted, and falls back to the
     existing urlName/urlEndpoint/events for anything not passed in — so
     sending just { merchantId, slot, enabled } flips enabled without
     touching the destination or minting new secrets. */
  const toggleEnabled = async (slot: string) => {
    const st = stateFor(slot);
    if (!st) return;
    try {
      const res = await fetch(`${API}/generateWebhookSecret`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ merchantId, slot, enabled: !st.enabled }),
      });
      if (!res.ok) { const d = await res.json(); throw new Error(d?.error?.message || "Update failed"); }
      await loadSlots();
    } catch (e: any) { setError(e.message || "Update failed"); }
  };

  const copyText = (text: string, tag: string) => {
    navigator.clipboard.writeText(text).then(() => { setCopied(tag); setTimeout(() => setCopied(null), 2000); });
  };

  const toggleEvent = (id: string, on: boolean) =>
    setSelected(p => { const n = new Set(p); on ? n.add(id) : n.delete(id); return n; });

  const pickSlot = (slot: string) => {
    setActiveSlot(slot);
    setSelected(defaultEventsFor(slot));
    const st = stateFor(slot);
    setUrlName(st?.urlName || ""); setUrlEndpoint(st?.urlEndpoint || "");
  };

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return WEBHOOK_SLOTS;
    return WEBHOOK_SLOTS
      .map(d => ({ ...d, events: d.events.filter(e => e.id.toLowerCase().includes(q) || e.desc.toLowerCase().includes(q)) }))
      .filter(d => d.events.length > 0);
  }, [query]);

  const step3Valid = urlName.trim().length > 0 && urlEndpoint.startsWith("https://");
  const ActiveIcon = slotDef(activeSlot).icon;

  return (
    <div style={{ background: "transparent", fontFamily: "'Space Grotesk',system-ui,sans-serif", fontWeight: 500, color: INK } as React.CSSProperties}>
      {/* Fonts load via a real stylesheet link, not an injected <style> block. */}
      <link rel="stylesheet" href={FONT_LINK} />

      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 16, marginBottom: 26 }}>
        <div>
          <h2 style={heading(24)}>Event Destinations</h2>
          <p style={mono({ color: FAINT, marginTop: 8 })}>
            {configured.length} of {WEBHOOK_SLOTS.length} configured
          </p>
        </div>
        <Btn variant="primary" onClick={() => openWizard()}>
          <Plus size={15} strokeWidth={2.8} />Add Destination
        </Btn>
      </div>

      {error && (
        <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }}
          style={plateStyle({ boxShadow: `0 0 0 2.5px ${RED}, 0 6px 0 ${RED_DEEP}`, marginBottom: 20, padding: "12px 16px", fontSize: 13, color: RED_DEEP, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 })}>
          <span style={{ display: "flex", alignItems: "center", gap: 9 }}>
            <AlertTriangle size={15} strokeWidth={2.4} />{error}
          </span>
          <CloseBtn onClick={() => setError(null)} size={30} />
        </motion.div>
      )}

      {loading && (
        <div style={{ textAlign: "center", padding: 60 }}>
          <Spinner size={30} />
          <div style={mono({ color: FAINT, marginTop: 14 })}>Loading destinations…</div>
        </div>
      )}

      {!loading && configured.length === 0 && (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
          style={plateStyle({ textAlign: "center", padding: isMobile ? "50px 20px" : "80px 40px" })}>
          <IconBead icon={Hexagon} size={62} iconSize={26} iconStrokeWidth={2.2} style={{ margin: "0 auto 20px" }} />
          <h3 style={heading(22, { marginBottom: 10 })}>No destinations yet</h3>
          <p style={{ color: SLATE, fontSize: 13, lineHeight: 1.7, maxWidth: 400, margin: "0 auto 24px" }}>
            Each destination handles one family of events and gets its own signing secret pair.
          </p>
          <Btn variant="primary" onClick={() => openWizard()}>
            <Plus size={15} strokeWidth={2.8} />Add Destination
          </Btn>
        </motion.div>
      )}

      {!loading && configured.length > 0 && (
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          {configured.map(d => (
            <SlotCard key={d.slot} def={d} state={stateFor(d.slot)!} rotating={rotateBusy === d.slot}
              onRotate={() => rotate(d.slot)} onToggle={() => toggleEnabled(d.slot)} onEdit={() => openWizard(d.slot)} />
          ))}
        </div>
      )}

      {!loading && configured.length > 0 && configured.length < WEBHOOK_SLOTS.length && (
        <div style={{ marginTop: 28 }}>
          <div style={mono({ color: FAINT, marginBottom: 12 })}>Not configured</div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 9 }}>
            {WEBHOOK_SLOTS.filter(d => !stateFor(d.slot)?.configured).map(d => {
              const I = d.icon;
              return (
                <Chip key={d.slot} onClick={() => openWizard(d.slot)}>
                  <I size={13} strokeWidth={2.4} />{d.label}
                </Chip>
              );
            })}
          </div>
        </div>
      )}

      {/* ── WIZARD ── */}
      <AnimatePresence>
        {showWizard && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={closeWizard}
            style={{ position: "fixed", inset: 0, zIndex: 1000, background: "rgba(6,14,56,.6)", backdropFilter: "blur(8px)", WebkitBackdropFilter: "blur(8px)", display: "flex", alignItems: "center", justifyContent: "center", padding: 20 } as React.CSSProperties}>
            <motion.div initial={{ opacity: 0, scale: 0.94, y: 20 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.96 }}
              onClick={e => e.stopPropagation()}
              style={plateStyle({ width: "100%", maxWidth: 660, maxHeight: "92vh", overflowY: "auto", padding: isMobile ? 22 : 34 })}>

              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, marginBottom: 24 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 12, minWidth: 0 }}>
                  <IconBead icon={step === 4 ? KeyRound : ActiveIcon} size={38} active />
                  <span style={{ fontSize: 19, fontFamily: "'Fredoka',sans-serif", fontWeight: 600 }}>
                    {step === 4 ? (secrets?.rotated ? "Secrets rotated" : "Destination created") : "Configure destination"}
                  </span>
                </div>
                <CloseBtn onClick={closeWizard} size={34} />
              </div>

              {step < 4 && <StepBar step={step} />}

              {/* STEP 1 — events */}
              {step === 1 && (
                <div>
                  <p style={{ fontSize: 13, color: SLATE, marginBottom: 14 }}>
                    Pick a family, then choose its events. Each family is its own destination with its own secret.
                  </p>
                  <TextField icon={Search} value={query} onChange={setQuery} placeholder="Find event by name or description…" style={{ marginBottom: 14 }} />

                  <div style={{ maxHeight: 380, overflowY: "auto", paddingRight: 6 }}>
                    {filtered.map(d => {
                      const isActive = d.slot === activeSlot;
                      const sel = isActive ? d.events.filter(e => selected.has(e.id)).length : 0;
                      const I = d.icon;
                      return (
                        <div key={d.slot} style={{ marginBottom: 8 }}>
                          <PickRow active={isActive} onClick={() => pickSlot(d.slot)}>
                            <Radio3D on={isActive} />
                            <IconBead icon={I} size={30} active={isActive} />
                            <span style={{ flex: 1, textAlign: "left", minWidth: 0 }}>
                              <span style={{ display: "flex", alignItems: "center", gap: 7, fontSize: 13.5, fontWeight: 600, fontFamily: "'Fredoka',sans-serif", color: isActive ? ACCENT : INK }}>
                                {d.label}
                                {d.v2 && <Tag>v2</Tag>}
                                {sel > 0 && <Tag tone="blue">{sel}</Tag>}
                              </span>
                              <span style={{ display: "block", fontSize: 11, color: FAINT, marginTop: 2 }}>
                                {d.events.length} events
                              </span>
                            </span>
                            <ChevronDown size={15} color={CHROME_DIM}
                              style={{ transform: isActive ? "rotate(180deg)" : "none", transition: "transform .25s", flexShrink: 0 }} />
                          </PickRow>

                          <AnimatePresence initial={false}>
                            {isActive && (
                              <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }}
                                transition={{ duration: .24, ease: [0.2, 0.9, 0.3, 1] }} style={{ overflow: "hidden" }}>
                                <div style={{ padding: "10px 4px 4px 16px" }}>
                                  <label style={evRowStyle({ isAll: true })}>
                                    <Check3D
                                      checked={d.events.every(e => selected.has(e.id))}
                                      onChange={v => setSelected(p => {
                                        const n = new Set(p);
                                        d.events.forEach(ev => v ? n.add(ev.id) : n.delete(ev.id));
                                        return n;
                                      })} />
                                    <span style={{ fontSize: 12, fontWeight: 600, color: FAINT }}>Select all {d.label} events</span>
                                  </label>
                                  {d.events.map((ev, idx) => (
                                    <label key={ev.id} style={evRowStyle({ isLast: idx === d.events.length - 1 })}>
                                      <Check3D checked={selected.has(ev.id)} onChange={v => toggleEvent(ev.id, v)} />
                                      <span style={{ minWidth: 0 }}>
                                        <span style={mono({ display: "block", fontSize: 11, color: INK, textTransform: "none", letterSpacing: 0, wordBreak: "break-all" })}>
                                          {ev.id}
                                          {!ev.live && <Tag tone="gold">soon</Tag>}
                                        </span>
                                        <span style={{ display: "block", fontSize: 11, color: FAINT, marginTop: 2 }}>{ev.desc}</span>
                                      </span>
                                    </label>
                                  ))}
                                </div>
                              </motion.div>
                            )}
                          </AnimatePresence>
                        </div>
                      );
                    })}
                  </div>

                  <div style={footStyle}>
                    <span style={{ fontSize: 13, color: SLATE }}>
                      {slotDef(activeSlot).label} · {selected.size} events
                    </span>
                    <Btn size="sm" disabled={selected.size === 0} onClick={() => setStep(2)}>
                      Continue<ChevronRight size={14} strokeWidth={2.8} />
                    </Btn>
                  </div>
                </div>
              )}

              {/* STEP 2 — destination type */}
              {step === 2 && (
                <div>
                  <p style={{ fontSize: 13, color: SLATE, marginBottom: 20 }}>Choose where these events are delivered.</p>
                  {[
                    { id: "webhook" as const, icon: ArrowLeftRight, label: "Webhook endpoint", sub: "POST signed events to your HTTPS endpoint", ready: true },
                    { id: "stream" as const, icon: Zap, label: "Ebuos event stream", sub: "Watch events live in your dashboard — coming soon", ready: false },
                  ].map(d => (
                    <PickRow key={d.id} active={destType === d.id} disabled={!d.ready} onClick={() => setDestType(d.id)} marginBottom={11}>
                      <Radio3D on={destType === d.id} />
                      <IconBead icon={d.icon} size={34} active={destType === d.id} />
                      <span style={{ flex: 1, textAlign: "left", minWidth: 0 }}>
                        <span style={{ display: "block", fontSize: 13.5, fontWeight: 600, fontFamily: "'Fredoka',sans-serif", color: INK }}>{d.label}</span>
                        <span style={{ display: "block", fontSize: 11.5, color: SLATE, marginTop: 2 }}>{d.sub}</span>
                      </span>
                    </PickRow>
                  ))}
                  <div style={footStyle}>
                    <Btn variant="ghost" size="sm" onClick={() => setStep(1)}>
                      <ChevronRight size={14} strokeWidth={2.8} style={{ transform: "rotate(180deg)" }} />Back
                    </Btn>
                    <Btn size="sm" onClick={() => setStep(3)}>
                      Continue<ChevronRight size={14} strokeWidth={2.8} />
                    </Btn>
                  </div>
                </div>
              )}

              {/* STEP 3 — configure */}
              {step === 3 && (
                <div>
                  <div style={recapStyle}>
                    <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <ActiveIcon size={14} strokeWidth={2.4} color={ACCENT} />
                      Family: <strong style={{ color: INK }}>{slotDef(activeSlot).label}</strong>
                    </span>
                    <span>Listening to: <strong style={{ color: INK }}>{selected.size} events</strong></span>
                  </div>
                  <FieldGroup label="Destination name">
                    <TextField value={urlName} onChange={setUrlName} placeholder={`production-${activeSlot}`} />
                  </FieldGroup>
                  <FieldGroup label="Endpoint URL">
                    <TextField value={urlEndpoint} onChange={setUrlEndpoint} placeholder={`https://yoursite.com/hooks/${activeSlot}`}
                      invalid={!!urlEndpoint && !urlEndpoint.startsWith("https://")} />
                    {urlEndpoint && !urlEndpoint.startsWith("https://") && (
                      <span style={{ fontSize: 11, color: RED_DEEP, marginTop: 5, display: "flex", alignItems: "center", gap: 5 }}>
                        <AlertTriangle size={11} />URL must start with https://
                      </span>
                    )}
                  </FieldGroup>
                  {wizError && (
                    <div style={{ padding: "11px 14px", borderRadius: 14, boxShadow: `inset 0 0 0 2px ${RED}`, fontSize: 12, color: RED_DEEP, marginBottom: 16, display: "flex", alignItems: "center", gap: 8 }}>
                      <AlertTriangle size={14} />{wizError}
                    </div>
                  )}
                  <div style={footStyle}>
                    <Btn variant="ghost" size="sm" onClick={() => setStep(2)}>
                      <ChevronRight size={14} strokeWidth={2.8} style={{ transform: "rotate(180deg)" }} />Back
                    </Btn>
                    <Btn size="sm" disabled={!step3Valid || busy} onClick={save}>
                      {busy ? <><Spinner size={13} />Saving…</> : <>Create destination</>}
                    </Btn>
                  </div>
                </div>
              )}

              {/* STEP 4 — secrets */}
              {step === 4 && secrets && (
                <div>
                  <div style={{ textAlign: "center", padding: "6px 0 22px" }}>
                    <IconBead icon={Check} size={62} tone="sage" iconSize={26} iconStrokeWidth={3.2} style={{ margin: "0 auto 16px" }} />
                    <p style={{ fontSize: 13, color: SLATE }}>
                      {slotDef(secrets.slot).label} destination saved. Both signing secrets are shown once.
                    </p>
                  </div>
                  <div style={warnStyle}>
                    <AlertTriangle size={15} strokeWidth={2.4} style={{ flexShrink: 0, marginTop: 1 }} />
                    <span><strong>Save these now.</strong> They can't be retrieved after you close this dialog. Use the test secret to verify test-mode signatures and the live secret for live mode.</span>
                  </div>
                  {[
                    { label: "Test signing secret", value: secrets.secret_test, tag: "test" },
                    { label: "Live signing secret", value: secrets.secret_live, tag: "live" },
                  ].map(s => (
                    <FieldGroup key={s.tag} label={s.label}>
                      <div style={{ display: "flex", gap: 8 }}>
                        <div style={secretBoxStyle}>{s.value}</div>
                        <Btn size="sm" onClick={() => copyText(s.value, s.tag)}
                          style={copied === s.tag ? { background: "#E8FBF2", color: SAGE_DEEP, boxShadow: `${BEVEL_SM}, 0 6px 0 ${SAGE_DEEP}` } : undefined}>
                          {copied === s.tag ? <Check size={14} strokeWidth={3} /> : <Copy size={14} strokeWidth={2.6} />}
                        </Btn>
                      </div>
                    </FieldGroup>
                  ))}
                  <div style={{ marginTop: 8, fontSize: 12, color: FAINT, lineHeight: 1.9 }}>
                    Endpoint: <span style={mono({ color: INK, textTransform: "none", letterSpacing: 0, wordBreak: "break-all" })}>{urlEndpoint}</span><br />
                    Events: <strong style={{ color: INK }}>{selected.size} selected</strong>
                  </div>
                  <Btn variant="primary" onClick={closeWizard} style={{ width: "100%", marginTop: 24 }}>
                    <Check size={15} strokeWidth={3} />I've saved both secrets
                  </Btn>
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

/* ══════════════ SLOT CARD ══════════════ */
function SlotCard({ def, state, rotating, onRotate, onToggle, onEdit }: {
  def: SlotDef; state: SlotState; rotating: boolean;
  onRotate: () => void; onToggle: () => void; onEdit: () => void;
}) {
  const [expanded, setExpanded] = useState(false);

  return (
    <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} style={plateStyle()}>
      <div style={{ display: "flex", alignItems: "flex-start", gap: 13 }}>
        <IconBead icon={def.icon} size={42} active={state.enabled} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 14.5, fontWeight: 600, fontFamily: "'Fredoka',sans-serif", color: INK }}>
            {state.urlName || def.label}
          </div>
          <div style={mono({ fontSize: 11.5, color: SLATE, wordBreak: "break-all", textTransform: "none", letterSpacing: 0, marginTop: 3 })}>
            {state.urlEndpoint}
          </div>
          <div style={{ fontSize: 11, color: FAINT, marginTop: 4 }}>
            {def.slot} · {state.events.length} events
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
          <Pill on={state.enabled}>{state.enabled ? "enabled" : "disabled"}</Pill>
          <Btn variant="ghost" size="sm" onClick={() => setExpanded(v => !v)}>
            <ChevronDown size={14} strokeWidth={2.6} style={{ transform: expanded ? "rotate(180deg)" : "none", transition: "transform .25s" }} />
          </Btn>
        </div>
      </div>

      <AnimatePresence initial={false}>
        {expanded && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }}
            transition={{ duration: .26, ease: [0.2, 0.9, 0.3, 1] }} style={{ overflow: "hidden" }}>
            <div style={perfStyle({ margin: "18px -22px" })} aria-hidden />
            <div style={{ marginBottom: 16 }}>
              <div style={mono({ color: FAINT, marginBottom: 9 })}>Listening to</div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                {state.events.map(ev => (
                  <span key={ev} style={{
                    fontFamily: "'IBM Plex Mono',monospace", fontWeight: 600, fontSize: 10, letterSpacing: 0, textTransform: "none",
                    background: ACCENT_TINT, color: ACCENT, padding: "4px 10px", borderRadius: 999, boxShadow: `inset 0 0 0 1.5px ${ACCENT}33`,
                  }}>
                    {ev}
                  </span>
                ))}
              </div>
            </div>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap", padding: "13px 0", boxShadow: `inset 0 2px 0 ${CHROME}` }}>
              <div style={{ display: "flex", alignItems: "center", gap: 11 }}>
                <IconBead icon={KeyRound} size={32} />
                <div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: INK, fontFamily: "'Fredoka',sans-serif" }}>Signing secrets</div>
                  <div style={{ fontSize: 11, color: FAINT, marginTop: 2 }}>
                    {state.has_secret
                      ? `Test + live pair · rotated ${state.rotated_at ? new Date(state.rotated_at * 1000).toLocaleDateString() : "unknown"}`
                      : "Not generated yet"}
                  </div>
                </div>
              </div>
              <Btn size="sm" onClick={onRotate} disabled={rotating} style={rotating ? { opacity: .6, cursor: "wait" } : undefined}>
                {rotating ? <><Spinner size={13} />Generating…</> : <><RefreshCw size={13} strokeWidth={2.8} />{state.has_secret ? "Rotate" : "Generate"}</>}
              </Btn>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", gap: 10, paddingTop: 13, boxShadow: `inset 0 2px 0 ${CHROME}` }}>
              <Btn variant="ghost" size="sm" onClick={onEdit}><PenLine size={13} strokeWidth={2.6} />Edit</Btn>
              <Btn variant="ghost" size="sm" onClick={onToggle}
                style={{ color: state.enabled ? RED_DEEP : SAGE_DEEP, boxShadow: `inset 0 0 0 2.5px ${state.enabled ? RED : SAGE}` }}>
                {state.enabled ? "Disable" : "Enable"}
              </Btn>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}